import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCreateMoleImageWithChoiceMutation } from '../../../store/apis/skinMolesApi';
import ImageCropper from './ImageCropper';

const AddImageToMoleModal = ({ show, onHide, mole, onImageAdded }) => {
  const { t, i18n } = useTranslation();
  const [createMoleImage, { isLoading }] = useCreateMoleImageWithChoiceMutation();

  const [formData, setFormData] = useState({
    file: null,
    distinctive_features: '',
    approximate_size_mm: '',
    description: '',
  });

  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors({ ...errors, file: t('skinMoles.addImageToMole.errors.selectValidImage') });
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, file: t('skinMoles.addImageToMole.errors.fileSizeLimit') });
        return;
      }

      setErrors({ ...errors, file: '' });
      
      // Show cropper instead of directly setting file
      setSelectedFile(file);
      setShowCropper(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleCropComplete = (croppedFile) => {
    setFormData({ ...formData, file: croppedFile });
    setShowCropper(false);
    setSelectedFile(null);
    
    // Create preview for cropped image
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(croppedFile);
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    setSelectedFile(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.file) {
      newErrors.file = t('skinMoles.addImageToMole.errors.selectImage');
    }

    if (
      formData.approximate_size_mm &&
      (isNaN(formData.approximate_size_mm) || formData.approximate_size_mm <= 0)
    ) {
      newErrors.approximate_size_mm = t('skinMoles.addImageToMole.errors.sizePositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Map language code to language_id: 'pl' = 1, 'en' = 2
      // Use same logic as SkinMolesComponent for consistency
      const currentLang = i18n.language;
      const isPolish = String(currentLang || '').toLowerCase().startsWith('pl');
      const languageId = isPolish ? 1 : 2; // Polish = 1, English = 2
      console.log('🌐 AddImageToMoleModal - Language mapping:', { 
        i18nLanguage: i18n.language, 
        currentLang,
        isPolish, 
        languageId 
      });
      
      const submitData = {
        file: formData.file,
        create_new_mole: false,
        user_mole_id: mole.uuid,
        distinctive_features: formData.distinctive_features.trim(),
        approximate_size_mm: formData.approximate_size_mm
          ? parseInt(formData.approximate_size_mm)
          : undefined,
        language: languageId.toString(), // Add language based on user's current language (backend expects string format)
      };

      // Add description if provided
      if (formData.description.trim()) {
        submitData.description = formData.description.trim();
      }

      await createMoleImage(submitData).unwrap();

      // Reset form
      setFormData({
        file: null,
        distinctive_features: '',
        approximate_size_mm: '',
        description: '',
      });
      setPreviewUrl('');
      setErrors({});

      // Close modal and notify parent
      onHide();
      if (onImageAdded) {
        onImageAdded();
      }
    } catch (error) {
      console.error('Failed to add image to mole:', error);
      setErrors({ submit: t('skinMoles.addImageToMole.errors.addImageFailed') });
    }
  };

  const handleClose = () => {
    setFormData({
      file: null,
      distinctive_features: '',
      approximate_size_mm: '',
      description: '',
    });
    setPreviewUrl('');
    setErrors({});
    onHide();
  };

  return (
    <>
    <Modal show={show} onHide={handleClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('skinMoles.addImageToMole.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className='mb-3'>
          <h6>
            {t('skinMoles.addImageToMole.addingImageTo')}{' '}
            <strong>{mole?.name || t('skinMoles.addImageToMole.unnamedMole')}</strong>
          </h6>
          <small className='text-muted'>
            {t('skinMoles.addImageToMole.moleCode')} {mole?.mole_code}
          </small>
        </div>

        <Form onSubmit={handleSubmit}>
          {/* File Upload */}
          <Form.Group className='mb-3'>
            <Form.Label>{t('skinMoles.addImageToMole.imageFileRequired')}</Form.Label>
            <Form.Control
              type='file'
              accept='image/*'
              onChange={handleFileChange}
              isInvalid={!!errors.file}
            />
            <Form.Control.Feedback type='invalid'>{errors.file}</Form.Control.Feedback>
          </Form.Group>

          {/* Image Preview */}
          {previewUrl && (
            <div className='mb-3'>
              <Form.Label>{t('skinMoles.addImageToMole.preview')}</Form.Label>
              <div className='text-center'>
                <img
                  src={previewUrl}
                  alt='Preview'
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                />
              </div>
            </div>
          )}

          {/* Distinctive Features */}
          <Form.Group className='mb-3'>
            <Form.Label>{t('skinMoles.addImageToMole.distinctiveFeatures')}</Form.Label>
            <Form.Control
              type='text'
              name='distinctive_features'
              value={formData.distinctive_features}
              onChange={handleInputChange}
              placeholder={t('skinMoles.addImageToMole.distinctiveFeaturesPlaceholder')}
            />
          </Form.Group>

          {/* Approximate Size */}
          <Form.Group className='mb-3'>
            <Form.Label>{t('skinMoles.addImageToMole.approximateSize')}</Form.Label>
            <Form.Control
              type='number'
              name='approximate_size_mm'
              value={formData.approximate_size_mm}
              onChange={handleInputChange}
              placeholder={t('skinMoles.addImageToMole.approximateSizePlaceholder')}
              min='1'
              step='0.1'
              isInvalid={!!errors.approximate_size_mm}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.approximate_size_mm}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Description (Optional) */}
          <Form.Group className='mb-3'>
            <Form.Label>{t('skinMoles.addImageToMole.description')}</Form.Label>
            <Form.Control
              as='textarea'
              name='description'
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('skinMoles.addImageToMole.descriptionPlaceholder')}
              rows={3}
            />
          </Form.Group>

          {/* Error Alert */}
          {errors.submit && (
            <Alert variant='danger' className='mb-3'>
              {t('skinMoles.errors.formError')}
            </Alert>
          )}

          {/* Submit Button */}
          <div className='d-flex justify-content-end gap-2'>
            <Button variant='secondary' onClick={handleClose} disabled={isLoading}>
              {t('skinMoles.addImageToMole.cancel')}
            </Button>
            <Button type='submit' variant='primary' disabled={isLoading || !formData.file}>
              {isLoading ? (
                <>
                  <Spinner
                    as='span'
                    animation='border'
                    size='sm'
                    role='status'
                    aria-hidden='true'
                    className='me-2'
                  />
                  {t('skinMoles.addImageToMole.addingImage')}
                </>
              ) : (
                t('skinMoles.addImageToMole.addImage')
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
    
    {/* Image Cropper Modal */}
    <ImageCropper
      show={showCropper}
      onHide={handleCropperClose}
      imageFile={selectedFile}
      onCropComplete={handleCropComplete}
    />
  </>
  );
};

export default AddImageToMoleModal;
